import { Card } from "antd";
import { FaUserMd, FaStethoscope, FaPlus } from "react-icons/fa";
import { IoMdRadioButtonOff, IoMdRadioButtonOn } from "react-icons/io";
import { useLanguage } from "../../context/LanguageContext";

export default function DoctorSelect({ doctors, onSelect, selectedDoctor }) {
    const { t } = useLanguage();
    
    if(doctors.length === 0){
        return(
            <Card className="border-2 border-dashed border-placeHolder bg-cardColor rounded-xl">
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <FaUserMd className="text-3xl text-placeHolder" />
                    </div>
                    <h3 className="text-lg font-medium text-textPrimary mb-2">
                        {t('doctorSelect.noDoctors.title')}
                    </h3>
                    <p className="text-placeHolder text-sm mb-4 max-w-md">
                        {t('doctorSelect.noDoctors.description')}
                    </p>
                    <div className="flex items-center gap-2 text-placeHolder text-xs">
                        <FaPlus className="text-sm" />
                        <span>{t('doctorSelect.noDoctors.footer')}</span>
                    </div>
                </div>
            </Card>
        )
    }
    
    return (
        <div className="space-y-3">
            {/* Header */}
            <h2 className="text-lg font-semibold text-textPrimary flex items-center gap-2 mb-4">
                <FaUserMd className="text-primary text-xl" />
                {t('doctorSelect.title')}
            </h2>

            {/* Doctor Cards */}
            <div className="grid grid-cols-1 gap-3">
                {doctors.map((doctor) => {
                    const isSelected = selectedDoctor?.doctorId === doctor.doctorId;
                    return (
                        <Card
                            key={doctor.doctorId}
                            onClick={() => onSelect(doctor)}
                            className={`border transition-all bg-cardColor text-textPrimary rounded-xl cursor-pointer hover:shadow-md ${
                                isSelected
                                    ? "border-textPrimary bg-primary text-white"
                                    : "border-placeHolder hover:border-primary"
                            }`}
                            bodyStyle={{ padding: "16px" }}
                        >
                            <div className="flex flex-col gap-3">
                                {/* Top Row: Radio Button and Doctor Name */}
                                <div className="flex items-center gap-3">
                                    {isSelected ? (
                                        <IoMdRadioButtonOn className="text-xl text-secondary" />
                                    ) : (
                                        <IoMdRadioButtonOff className="text-xl text-textPrimary" />
                                    )}
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold">
                                           {t("common.dr")} {doctor.doctorName}
                                        </h3>
                                        {doctor.specialization && (
                                            <div className={`flex items-center gap-2 mt-1 ${
                                                isSelected ? "text-white/90" : "text-placeHolder"
                                            }`}>
                                                <FaStethoscope className="text-sm" />
                                                <span className="text-sm font-medium">
                                                    {doctor.specialization}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Working Days Info */}
                                <div className={`text-sm ${
                                    isSelected ? "text-white/80" : "text-placeHolder"
                                }`}>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">{t('doctorSelect.availableDays')}:</span>
                                        {doctor.workingDays && doctor.workingDays.length > 0 ? (
                                            <span>
                                                {doctor.workingDays
                                                    .filter(day => day.isWorking)
                                                    .map(day => {
                                                        const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                                                        return t(`doctorSelect.days.${dayKeys[day.dayOfWeek]}`);
                                                    })
                                                    .join(', ')
                                                }
                                            </span>
                                        ) : (
                                            <span className="italic">{t('doctorSelect.noSchedule')}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}